import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'i18n-lite';

import { LabledInput, InputErrors } from 'ui/Input';
import Button from 'ui/Button';
import Checkbox from 'ui/Checkbox';
import Spinner from 'ui/Spinner';
import LoginLayout from 'components/LoginLayout';
import { targetValue, prevent } from 'utils/events';
import { Meeting, Terms as TermsModel } from 'store/models';
import Terms from 'components/Terms';
import { loadRoom, getTermsByMeet } from 'api';
import useValidator, { required, email as vEmail, accepted } from 'use-validatorjs';
import Header from 'components/Header';

import textStyles from 'ui/Text.module.scss';
import styles from './Pages.module.scss';
import formStyles from 'ui/Form.module.scss';

interface LoginProps {
  header?: React.ReactNode;
  onUser: (e: { email: string; name: string }) => void;
  entityType?: 'meeting' | 'room';
  entityId?: string;
  isBackButton?: boolean;
}

const Login: React.FC<LoginProps> = ({
  header,
  onUser,
  entityType,
  entityId,
  isBackButton = true,
}) => {
  const { t } = useTranslation('joinPage');
  const { t: presT } = useTranslation('presentationPage');
  const { t: vT } = useTranslation('validations');

  const [terms, setTerms] = useState<TermsModel>();
  const [meet, setMeet] = useState<Meeting>();
  const [name, setName] = useState('');
  const [isAcceptTerms, setAccept] = useState(false);
  const [error, setError] = useState<'404' | 'unhandled'>();
  const [email, setEmail] = useState('');
  const { isErrors, showErrors, isShowErrors, errors } = useValidator(
    vT,
    {
      isAcceptTerms,
      email,
      name,
    },
    {
      isAcceptTerms: terms ? accepted : [],
      email: [...(meet?.isGuestEmailRequired ?? false ? [required] : []), vEmail],
      name: required,
    },
  );

  const handleSubmit = useCallback(() => {
    if (isErrors) {
      showErrors();

      return;
    }

    onUser({
      name,
      email,
    });
  }, [name, email, onUser, isErrors, showErrors]);

  useEffect(() => {
    if (!entityId) {
      return;
    }

    Promise.allSettled([loadRoom({ room: entityId }), getTermsByMeet({ roomId: entityId })])
      .then(([roomRes, termsRes]) => {
        if (roomRes.status === 'rejected') {
          throw roomRes.reason;
        }

        if (termsRes.status === 'fulfilled') {
          setTerms(termsRes.value);
        }

        const {
          value: { meet },
        } = roomRes;

        setError(undefined);
        setMeet(meet);
      })
      .catch((err) => {
        switch (err.status) {
          case 404: {
            setError('404');
            break;
          }

          default: {
            setError('unhandled');
          }
        }
      });
  }, [entityId]);

  return (
    <LoginLayout
      isBackButton={isBackButton}
      isWide={!!terms}
      heading={header ? header : <Header entityType={entityType} entityId={entityId} />}
    >
      {meet ? (
        <form className={formStyles.form} onSubmit={prevent(handleSubmit)}>
          <div className={styles.welcome}>
            <h1 className={styles.welcomeHeading}>{t('title')}</h1>
            <p className={styles.welcomeDetails}>{t('details')}</p>
          </div>
          <div className={styles.loginFromGrid}>
            <div className={styles.loginFromGridColumn}>
              <LabledInput
                autoFocus
                tabIndex={1}
                className={formStyles.formGroup}
                placeholder={t('nameInput.placeholder')}
                value={name}
                onInput={targetValue(setName)}
                errors={errors.name}
              >
                {t('nameInput.label')}
              </LabledInput>
              <LabledInput
                tabIndex={2}
                className={formStyles.formGroup}
                placeholder={t('emailInput.placeholder')}
                value={email}
                onInput={targetValue(setEmail)}
                errors={errors.email}
              >
                {t('emailInput.label')}
              </LabledInput>
            </div>
            {terms && (
              <div className={styles.loginFromGridColumn}>
                <Terms className={styles.terms} terms={terms} />
                <Checkbox value={isAcceptTerms} onChange={setAccept}>
                  {t('terms')}
                </Checkbox>
                <InputErrors errors={errors.isAcceptTerms} />
              </div>
            )}
          </div>
          <Button
            isBlock
            className={terms ? styles.welcomeJoinButton : undefined}
            isDisabled={isErrors && isShowErrors}
            onClick={prevent(handleSubmit)}
          >
            {t('submit')}
          </Button>
        </form>
      ) : !error ? (
        <Spinner isCenter />
      ) : (
        <div className={textStyles.center}>
          <h2>{presT(`errors.${error}`)}</h2>
        </div>
      )}
    </LoginLayout>
  );
};

export default Login;
